import { useEffect, useState } from 'react';
import { scoreTone, toneColour } from '../lib/format';
import { tx } from '../i18n';

interface ScoreGaugeProps {
  score: number;
  size?: number;
  label?: string;
  animate?: boolean;
}

/**
 * Circular score ring.
 *
 * <p>The arc animates through a stroke dash offset rather than JavaScript, and the whole thing is
 * one element with an explicit value for screen readers.
 */
export default function ScoreGauge({
  score,
  size = 168,
  label,
  animate = true,
}: ScoreGaugeProps) {
  const [displayed, setDisplayed] = useState(animate ? 0 : score);

  useEffect(() => {
    if (!animate) {
      setDisplayed(score);
      return undefined;
    }
    const timer = window.setTimeout(() => setDisplayed(score), 60);
    return () => window.clearTimeout(timer);
  }, [score, animate]);

  const strokeWidth = Math.max(8, Math.round(size * 0.075));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, displayed));
  const offset = circumference - (clamped / 100) * circumference;
  const colour = toneColour(scoreTone(score));

  return (
    <div
      className="gauge"
      role="meter"
      aria-valuenow={score}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={tx(`${label ?? 'Overall score'}: ${score} out of 100`, `${label ?? 'Genel puan'}: 100 üzerinden ${score}`,
    `${label ?? 'Ocena ogólna'}: ${score} na 100`,
    `${label ?? 'Pontuação global'}: ${score} em 100`,
    `${label ?? 'Puntuación global'}: ${score} sobre 100`)}
      style={{ width: size, height: size }}
    >
      <svg className="gauge__svg" width={size} height={size} aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        <circle
          className="gauge__arc"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={colour}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="gauge__centre">
        <span className="gauge__score" style={{ color: colour, fontSize: Math.round(size * 0.28) }}>
          {score}
        </span>
        <span className="gauge__unit">{tx('out of 100', '100 üzerinden',
    'na 100',
    'em 100',
    'sobre 100')}</span>
      </div>
    </div>
  );
}
